import { CSSProperties, RefObject, useEffect, useRef } from 'react';
import CreatableSelect, { CreatableProps } from 'react-select/creatable';
import { GroupBase, MultiValueRemoveProps, components } from 'react-select';
import { isObject } from 'underscore';

import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { mergeRefs } from '@/utils/merge-refs';
import { ReactSelectInstance, ReactSelectInstanceRefObject } from './custom-react-select.types';

import styles from './custom-react-select.module.css';

interface CreatableReactSelectOptions extends CreatableProps<unknown, boolean, GroupBase<unknown>> {
  wrapperClass?: string;
  clearAllClass?: string;
  disableClearWhenNotFocused?: boolean;
  ref?: ReactSelectInstanceRefObject;
}

const CreatableReactSelect = ({
  clearAllClass,
  disableClearWhenNotFocused,
  wrapperClass,
  ref,
  ...rest
}: CreatableReactSelectOptions) => {
  const selectRef = useRef<ReactSelectInstance>(null);

  // force scrolling to input on focus
  useEffect(() => {
    const select = selectRef?.current;
    if (!select) return;

    const controlRef = select?.controlRef;
    const inputRef = select?.inputRef;
    if (!inputRef || !controlRef) return;

    const valueContainer = controlRef.firstElementChild;
    if (valueContainer) {
      valueContainer.scrollTo({
        top: valueContainer.scrollHeight,
        left: valueContainer.scrollLeft,
      });
    }

    const handleFocus = () => {
      if (valueContainer) {
        valueContainer.scrollTo({
          top: valueContainer.scrollHeight,
          left: valueContainer.scrollLeft,
        });
      }
    };

    inputRef.addEventListener('focus', handleFocus);

    return () => {
      inputRef.removeEventListener('focus', handleFocus);
    };
  }, [selectRef]);

  return (
    <div className={`${styles['custom-react-select-wrapper']} ${wrapperClass || ''}`}>
      {rest?.isSearchable && (
        <div
          data-testid="custom-react-select-search-icon-wrapper"
          className={styles['custom-react-select-search-icon-wrapper']}
        >
          <Icon name={ICONS.SEARCH} />
        </div>
      )}

      <CreatableSelect
        {...rest}
        ref={mergeRefs(selectRef, ref)}
        components={{
          ...rest?.components,
          MultiValueRemove: (options) => {
            handleScrollToFocusedRemoveIcon(options, selectRef);
            const RemoveEl = rest?.components?.MultiValueRemove || components.MultiValueRemove;
            return <RemoveEl {...options} />;
          },
          ClearIndicator:
            rest?.components?.ClearIndicator ||
            ((props) => {
              const { innerProps, ...restOfProps } = props;
              return (
                <components.ClearIndicator {...restOfProps} innerProps={{}}>
                  <div
                    {...innerProps}
                    className={`${clearAllClass || ''} ${props.innerProps.className || ''}`}
                    style={{ ...innerProps.style, display: 'flex' }}
                    onMouseDown={(e) => {
                      if (disableClearWhenNotFocused && !props.isFocused) {
                        return;
                      }
                      innerProps?.onMouseDown?.(e);
                    }}
                    onTouchEnd={(e) => {
                      if (disableClearWhenNotFocused && !props.isFocused) {
                        return;
                      }
                      innerProps?.onTouchEnd?.(e);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        props.clearValue();
                        // refocus the input after clearing the value
                        const inputRef = ref?.current?.inputRef;
                        if (inputRef) {
                          inputRef.focus();
                        }
                      }
                    }}
                  >
                    <Icon name={ICONS.CLOSE} size={14} />
                  </div>
                </components.ClearIndicator>
              );
            }),
        }}
      />
    </div>
  );
};

export default CreatableReactSelect;

// We don't use buttons with react-select multi value options or we could end up with
// poorly accessibility because if a list is super long the user will have to tab through every single clear icon.
// Because of that, react-select uses div's. However, there's no obvious attribute letting us know it's "focused"
// so we track the background color and if it has one, then we scroll to that element.
// Note: this IS fragile, if we end up adding a default backgroundColor to multi value remove icons, then this will break.
const handleScrollToFocusedRemoveIcon = (
  options: MultiValueRemoveProps<unknown, boolean, GroupBase<unknown>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectRef: RefObject<any>
) => {
  if (!selectRef) return;

  const innerProps = isObject(options?.innerProps) ? options.innerProps : null;
  const css = innerProps && 'css' in innerProps && innerProps.css;
  const backgroundColor =
    isObject(css) && 'backgroundColor' in css && (css.backgroundColor as CSSProperties['backgroundColor']);

  // if a remove icon is "focused" it contains a background color, otherwise it's undefined
  // so anything beyond this means we are "focused" on a multi value remove element
  if (!backgroundColor) return;

  const select = selectRef.current;
  if (select) {
    const label = options?.innerProps?.['aria-label'];
    if (!label?.startsWith('Remove ')) {
      return;
    }
    const controlRef = select.controlRef;
    if (!controlRef) return;

    const labelElement = controlRef.querySelector(`[aria-label="${label}"]`) as HTMLElement | undefined;
    const scrollableValueContainer = controlRef.firstElementChild;

    if (labelElement && scrollableValueContainer) {
      const scrollableValueContainerRect = scrollableValueContainer.getBoundingClientRect();
      const labelRect = labelElement.getBoundingClientRect();
      const offsetTop = labelRect.top - scrollableValueContainerRect.top + scrollableValueContainer?.scrollTop;
      scrollableValueContainer.scrollTop = offsetTop - 6; // 6 accounts for spacing
    }
  }
};
